/**
 * Constantes de rendu : hauteurs de prismes, teintes des zones, contours,
 * repères. Séparées de `config.ts`, qui n'aurait plus tenu sur un écran.
 *
 * Elles n'ont rien en commun avec les réglages d'entrée ou de caméra sinon
 * d'être des nombres posés à la main — et ce sont celles qui bougeront le jour
 * où une direction artistique sera choisie.
 */
export const renderSettings = {
  /**
   * Altitude de la base commune des prismes, sous le minimum de
   * `map.elevationHeights` ([TD-028]) : sans marge, la plaine aurait une
   * épaisseur nulle et serait invisible.
   */
  terrainBase: -1,

  /**
   * Teinte de la zone atteignable ([TD-045]). L'intensité décroît avec le
   * coût : le proche ressort, le lointain s'estompe.
   */
  /**
   * Zones : un contour par tuile, jamais un aplat ([GD-026]). Le
   * remplissage est réservé à ce qui est proposé — le trajet, la cible.
   * Épaisseur en mètres, un hexagone faisant 1 m de rayon ; plus fine que la
   * désignation, qui doit rester la marque la plus forte de l'écran.
   */
  movementRange: {
    color: 0x7fb3e0,
    thickness: 0.1,
  },
  attackRange: {
    color: 0xd9705a,
    thickness: 0.1,
  },

  /** Trajet proposé avant confirmation ([GD-013]). Rempli, dans le bleu de sa zone. */
  movementPath: {
    color: 0x7fb3e0,
    ratio: 0.75,
  },

  /** Cible visée et sa pétale ([GD-011]). Remplie, dans le rouge de sa zone. */
  attackTarget: {
    color: 0xd9705a,
    ratio: 0.8,
  },

  /**
   * Brouillard de guerre ([GD-047]).
   *
   * Ce qu'on n'a jamais vu est une masse posée sur la carte ([GD-054]).
   * `unknown` donne son ton au parchemin qui l'habille ([GD-067]) ; la masse
   * elle-même ne se dessine plus.
   *
   * Ce qu'on a vu et qu'on ne surveille plus relève de `shadow`, plus bas :
   * un volume de verre au même plafond, et non une teinte de plus.
   */
  fog: {
    unknown: 0xd6c7a5,
    /**
     * Altitude du toit de la masse, en mètres. Au-dessus du plus haut relief
     * possible — un sommet à `max(elevationHeights) + peakRise` — sans quoi les
     * massifs perceraient le voile et se compteraient de loin ([GD-054]).
     */
    ceiling: 2.9,
  },

  /**
   * La nappe de parchemin qui habille le brouillard ([GD-067]).
   *
   * `metresPerTile` dit tous les combien de mètres la texture se répète. Elle
   * est plaquée en coordonnées monde, donc c'est cette seule valeur qui décide
   * de l'échelle du grain — et non la taille d'une case.
   *
   * `reach` borne les aspérités du contour, **en part du rayon d'un hexagone**.
   * Une moitié : c'est ce qu'il faut pour que le grain fin et les anses tiennent
   * tous deux à pleine force dans le même budget. Ce qu'elles débordent est
   * masqué sur la case voisine, qui appartient au liseré découvert ([GD-056])
   * — au plus 44 cm mesurés, sur une case qui en fait 1,73 de large. La
   * désignation n'en souffre pas : le pointeur vise les prismes de la masse,
   * jamais la nappe. En part et non en mètres : ce fichier ne connaît pas la
   * taille d'une case.
   */
  sheet: {
    /** Tous les combien de mètres la texture de parchemin se répète. */
    metresPerTile: 22,
    /**
     * De combien le toit déborde de l'hexagone. Les prismes de la masse sont
     * eux-mêmes 2 % plus larges ([TD-059]) : sans cela leur sable dépasserait
     * du parchemin.
     */
    swell: 1.03,
    /**
     * Largeur de la frange où le bord se découpe, en rayons d'hexagone. C'est
     * la seule bande que le découpage peut mordre : au-delà rien n'existe, en
     * deçà la couverture vaut un et rien ne s'efface.
     */
    fringe: 0.9,
    /**
     * Couverture au bord extérieur de la frange. Doit rester **sous** `cut`,
     * sans quoi le bord extérieur survivrait et la frange se verrait telle
     * quelle, avec son contour d'hexagone.
     */
    rim: 0.28,
    /**
     * Seuil de découpe. Doit rester **sous** le plancher du bruit, sans quoi
     * des trous s'ouvriraient au coeur de la zone où la couverture vaut un.
     */
    cut: 0.3,
    /**
     * Le bruit qui déchire, au pixel. `metres` dit tous les combien de mètres
     * il se répète : c'est lui, et non une découpe de géométrie, qui décide de
     * la finesse du déchirement. Son `floor` tient le coeur des zones plein.
     */
    edge: {
      size: 256,
      seed: 71053,
      period: 6,
      octaves: 5,
      floor: 0.35,
      metres: 2.6,
    },
    paper: {
      size: 256,
      seed: 20260831,
      period: 9,
      octaves: 4,
      grain: 0.1,
    },
  },

  /**
   * La zone d'ombre : découverte, plus surveillée ([GD-057]). Un volume de
   * verre monté au plafond du brouillard, pas une teinte — l'ombre doit avoir
   * le même relief que la masse qui la prolonge.
   *
   * L'opacité fait tout le travail d'assombrissement, toit et parois teintés
   * pareil : le terrain se lit dessous, on ignore seulement qui l'occupe.
   */
  shadow: {
    /**
     * Le toit de l'ombre passe **sous** celui de la masse ([TD-060]). Les deux
     * étaient à la même altitude : leurs plafonds se disputaient la profondeur
     * partout où ils se touchaient, et l'ombre teintait le parchemin en
     * clignotant. Dix centimètres sur près de trois mètres ne se voient pas.
     */
    drop: 0.1,
    color: 0x18222e,
    /**
     * Le tri des faces avant garantit qu'un rayon ne traverse jamais plus de
     * deux vitres : le coeur d'une zone s'assombrit de 28 %, son bord de 48 %.
     */
    opacity: 0.28,
  },

  /**
   * Ce qu'un sommet gagne en hauteur sur sa bande ([GD-053]). Purement visuel :
   * une montagne cernée de montagnes se dresse, sans rien changer au jeu.
   *
   * Doit rester **sous** `flightAltitude` : un aéronef survolant une montagne
   * voisine passe à `1.8 + flightAltitude`, il doit rester au-dessus du sommet.
   */
  peakRise: 0.9,

  /**
   * L'anneau de vie des pastilles ([GD-063]). Vert tant que l'unité tient, et
   * orange dans le dernier cinquième — le seuil où elle sort du combat, ou, si
   * elle vole, celui où elle n'est plus loin de tomber.
   *
   * En chaîne CSS et non en entier : ces deux-là ne servent qu'au DOM.
   */
  gauge: {
    healthy: "#5fbf72",
    critical: "#e08a34",
  },

  /** Hauteur du jeton d'un aéronef au-dessus du sol, en mètres ([GD-048]). */
  flightAltitude: 2.4,

  /**
   * Contour de la tuile survolée ([GD-052]). Un contour et non une teinte :
   * éclaircir la case révélait son terrain à travers le brouillard, ce qui
   * donnait une carte à qui promenait sa souris.
   *
   * Plus fin et plus discret que la désignation, qu'il ne doit pas concurrencer
   * lorsque les deux tombent sur la même case.
   */
  hover: {
    color: 0xdfe6ec,
    thickness: 0.08,
    /** Au-dessus de la désignation : le survol se voit toujours. */
    lift: 0.03,
  },

  /**
   * Le fantôme de pose d'un bâtiment ([GD-068]) : vert là où l'on peut poser,
   * rouge ailleurs. Assez saturés pour se lire par-dessus n'importe quel
   * terrain, la palette de celui-ci étant sourde.
   */
  build: {
    allowed: 0x5fbf72,
    refused: 0xd9705a,
  },

  /** Contour de la tuile désignée ([TD-043]). */
  selection: {
    /**
     * Vert franc mais non fluorescent. Il doit se détacher de la plaine
     * (0x6b7a4a, olive sombre) et de la forêt (0x3d4f2f) : il est donc plus
     * clair et plus vert qu'elles, sans saturer. Le matériau n'étant pas
     * éclairé, il ressort déjà davantage que sa valeur brute ne le laisse
     * croire.
     */
    color: 0x74c46a,
    /** Épaisseur du contour, en mètres. Un hexagone fait 1 m de rayon. */
    thickness: 0.16,
    /** Décollement au-dessus de la face supérieure, contre le z-fighting. */
    lift: 0.02,
  },

  /**
   * Repères de zone posés hors carte ([TD-039]). Volontairement saturés :
   * la palette du terrain est sourde, ils doivent se lire comme une annotation
   * et non comme un terrain de plus.
   */
  zoneMarkerColors: {
    camp: 0xe07a2f,
    noMansLand: 0xc23b22,
    /** Rangée centrale r = 0. Elle existe toujours : la hauteur vaut 2H+1. */
    frontLine: 0xf2c744,
  },
} as const;
